import { useState } from 'react'
import { Form, Input, Button, Card, Typography, Space, Divider, Alert } from 'antd'
import { UserOutlined, MailOutlined, LockOutlined, GoogleOutlined } from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { apiService, SignupCredentials } from '@/lib/api'

const { Title, Text } = Typography

interface FormSignupCredentials extends Omit<SignupCredentials, 'first_name' | 'last_name' | 'password_confirmation'> {
  name: string
  confirmPassword: string
}

export default function SignupPage() {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const signupMutation = useMutation({
    mutationFn: (credentials: SignupCredentials) => apiService.register(credentials),
    onSuccess: (response) => {
      console.log('Signup response:', response)

      if (response.success && response.user && response.token) {
        setSuccessMessage('Account created successfully! Redirecting to onboarding...')
        setErrorMessage(null)

        localStorage.setItem('authToken', response.token)

        const userWithOnboarding = {
          ...response.user,
          needs_onboarding: true
        }
        localStorage.setItem('user', JSON.stringify(userWithOnboarding))

        console.log('Stored user data:', userWithOnboarding)

        setTimeout(() => {
          console.log('Redirecting to onboarding...')
          navigate('/onboarding')
        }, 100)
      } else if (response.success && response.data) {
        setSuccessMessage('Account created successfully! Redirecting to onboarding...')
        setErrorMessage(null)

        localStorage.setItem('authToken', response.data.token)

        const userWithOnboarding = {
          ...response.data.user,
          needs_onboarding: true
        }
        localStorage.setItem('user', JSON.stringify(userWithOnboarding))

        setTimeout(() => {
          navigate('/onboarding')
        }, 100)
      } else {
        console.log('Unexpected response structure:', response)
        const error = response.error || response.message || response.errors?.join(', ') || 'Signup failed. Please try again.'
        setErrorMessage(error)
        setSuccessMessage(null)
      }
    },
    onError: (error: Error) => {
      console.error('Signup error:', error)
      setErrorMessage(error.message || 'Signup failed. Please try again.')
      setSuccessMessage(null)
    },
  })

  const handleGoogleSignup = async () => {
    try {
      await apiService.initGoogleOAuth()
    } catch (error) {
      setErrorMessage('Failed to initiate Google signup')
    }
  }

  const onFinish = (values: FormSignupCredentials) => {
    if (values.password !== values.confirmPassword) {
      setErrorMessage('Passwords do not match!')
      return
    }

    const nameParts = values.name.trim().split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    const credentials: SignupCredentials = {
      email: values.email,
      password: values.password,
      first_name: firstName,
      last_name: lastName,
      password_confirmation: values.confirmPassword,
    }

    signupMutation.mutate(credentials)
  }

  return (
    <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" style={{ background: '#111827', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <div className="text-center mb-6">
          <Title level={2} className="mb-2">Create Account</Title>
          <Text type="secondary">Join us today and get started</Text>
        </div>

        {errorMessage && (
          <Alert
            message={errorMessage}
            type="error"
            showIcon
            closable
            onClose={() => setErrorMessage(null)}
            className="mb-4"
          />
        )}

        {successMessage && (
          <Alert
            message={successMessage}
            type="success"
            showIcon
            className="mb-4"
          />
        )}

        <Form
          form={form}
          name="signup"
          onFinish={onFinish}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[
              { required: true, message: 'Please input your full name!' },
              { min: 2, message: 'Name must be at least 2 characters!' }
            ]}
          >
            <Input prefix={<UserOutlined />} placeholder="Enter your full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="Enter your email" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 6, message: 'Password must be at least 6 characters!' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Enter your password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Passwords do not match!'))
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm your password" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="w-full"
              loading={signupMutation.isPending}
            >
              {signupMutation.isPending ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </Form.Item>
        </Form>

        <Divider>or</Divider>

        <Button
          type="default"
          icon={<GoogleOutlined />}
          onClick={handleGoogleSignup}
          className="w-full mb-4"
          size="large"
        >
          Sign up with Google
        </Button>

        <div className="text-center">
          <Space>
            <Text type="secondary">Already have an account?</Text>
            <Link to="/login" className="text-cyan-400 hover:text-cyan-300">
              Log in
            </Link>
          </Space>
        </div>
      </Card>
    </div>
  )
}
